import React from 'react'

import { TextClamp } from '../components/text/TextClamp'
import { roundHalfAwayFromZero } from '../foundations/producer-rounding'
import { clampArray } from '../foundations/slide-limits'
import { sanitizeNarrativeText } from '../foundations/text-sanitize'
import { SlideFrame } from '../layouts/SlideFrame'
import { CoverBackground } from '../primitives/CoverBackground'
import { registerSlide } from './registry'
import type { EditableSlideProps } from './contracts'
import type { OutroSection, OutroSlideData } from './renderer-internal-types'

const COVER_BG = 'var(--ds-cover-bg)'
const DIVIDER = '#384242'
const TEXT_MUTED = '#BDBDBD'
const CHIP_BG = 'rgba(255,255,255,0.92)'
const CHIP_TEXT = '#111111'
const BAR_BG = 'var(--slide-text-muted)'
const CHART_PRIMARY = 'var(--slide-chart-1)'
const CHART_SOFT = 'var(--slide-chart-7)'
const WHOLE: React.CSSProperties = { whiteSpace: 'normal', overflowWrap: 'break-word', minWidth: 0 }

function SectionTable(props: {
  section?: OutroSection
  testId: string
  /**
   * D17: threaded down from the slide so the row-label clamp below can drop its Radix
   * trigger in a static export, where no pointer or focus ever arrives.
   */
  renderMode?: 'interactive' | 'static'
}) {
  const { section, testId, renderMode } = props
  if (!section) return null
  const rows = clampArray(section.rows || [], 'outro', 'rows')
  const chips = [...(section.topics || []), ...(section.details || [])].slice(0, 8)

  return (
    <section data-testid={testId} style={{ display: 'flex', flexDirection: 'column', gap: 6, minWidth: 0 }}>
      <TextClamp minLines={1} maxLines={1} lineHeight="1.4" style={{ fontSize: 11, fontWeight: 700, color: '#FFFFFF' }}>
        {section.title}
      </TextClamp>
      <div style={{ height: 1.5, background: DIVIDER }} />
      {rows.map((row, index) => (
        <div key={`${row.label}-${index}`}>
          <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: 54, alignItems: 'center', minHeight: 17 }}>
            <TextClamp tooltipOnTruncate renderMode={renderMode} minLines={1} maxLines={1} lineHeight="1.2" style={{ fontSize: 11, color: TEXT_MUTED }}>{row.label}</TextClamp>
            <TextClamp minLines={1} maxLines={1} lineHeight="1.2" style={{ fontSize: 12, fontWeight: 600, color: '#FFFFFF' }}>{row.value}</TextClamp>
          </div>
          <div style={{ height: 1, background: DIVIDER, opacity: 0.5, marginTop: 4 }} />
        </div>
      ))}
      {chips.length ? (
        <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: 54, alignItems: 'start', marginTop: 2 }}>
          <TextClamp minLines={1} maxLines={1} lineHeight="1.2" style={{ fontSize: 11, color: TEXT_MUTED }}>
            {/* legacy reader only: a deck built since AI part 3 stamps `chipsLabel`. */}
            {section.chipsLabel ?? 'Details'}
          </TextClamp>
          <div data-testid={`${testId}-chips`} style={{ display: 'flex', flexWrap: 'wrap', gap: '6px 8px', minWidth: 0 }}>
            {chips.map((chip) => (
              <span key={chip} style={{ borderRadius: 20, background: CHIP_BG, color: CHIP_TEXT, padding: '4px 12px', fontSize: 11, fontWeight: 600, whiteSpace: 'nowrap' }}>
                {chip}
              </span>
            ))}
          </div>
        </div>
      ) : null}
    </section>
  )
}

function DistributionSection(props: {
  title: string
  rows: Array<{ label: string; value: number; count?: number | null }>
  caption?: string
  testId?: string
}) {
  const rows = props.rows.slice(0, 5)
  return (
    <section data-testid={props.testId ?? 'outro-distribution'} style={{ display: 'flex', flexDirection: 'column', gap: 6, minWidth: 0 }}>
      {/* A title and a label print whole and wrap, never cut: a sample question's
          title is the question's own report title. */}
      <div style={{ ...WHOLE, fontSize: 11, fontWeight: 700, lineHeight: 1.4, color: '#FFFFFF' }}>{props.title}</div>
      <div style={{ height: 1.5, background: DIVIDER }} />
      {rows.map((row) => (
        <div key={row.label} style={{ display: 'grid', gridTemplateColumns: '216px 1fr 88px', gap: 12, alignItems: 'center' }}>
          <div style={{ ...WHOLE, fontSize: 12, lineHeight: 1.2, color: TEXT_MUTED }}>{row.label}</div>
          <div style={{ height: 8, borderRadius: 2, background: BAR_BG, overflow: 'hidden' }}>
            <div style={{ height: '100%', width: `${Math.max(2, Math.min(100, row.value))}%`, background: `linear-gradient(90deg, ${CHART_PRIMARY}, ${CHART_SOFT})`, borderRadius: 2 }} />
          </div>
          <TextClamp minLines={1} maxLines={1} lineHeight="1.2" style={{ fontSize: 12, fontWeight: 700, color: '#FFFFFF' }}>
            {`${roundHalfAwayFromZero(row.value)}%`}
          </TextClamp>
        </div>
      ))}
      {props.caption ? (
        <TextClamp
          data-testid="outro-distribution-caption"
          minLines={1}
          maxLines={1}
          lineHeight="1.2"
          style={{ fontSize: 11, color: TEXT_MUTED, marginTop: 2 }}
        >
          {props.caption}
        </TextClamp>
      ) : null}
    </section>
  )
}

// D17: this slide has no inline editing, so it takes only the render-context half of the
// shared slide contract rather than advertising `editable` / `onEdit` it does not honor.
function OutroSlideComponent(props: OutroSlideData & Pick<EditableSlideProps, 'renderMode'>) {
  const logoUrl = props.clientLogoUrl ?? null
  const renderMode = props.renderMode
  const distributions = clampArray(props.distributions || [], 'outro', 'distributions')
  const sampleQuestions = clampArray(props.sampleQuestions || [], 'outro', 'sampleQuestions')

  return (
    <SlideFrame fullBleed background={COVER_BG}>
      <CoverBackground idPrefix="outro-cover" />
      {/* The cover's own scrim leaves the right side bright. This slide's
          copy and bars run across that side, so a further dark layer keeps
          them readable while the brand field still shows through. */}
      <div
        data-testid="outro-readability-scrim"
        style={{
          position: 'absolute',
          inset: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          pointerEvents: 'none',
        }}
      />
      <div
        data-testid="outro-border"
        style={{
          position: 'absolute',
          inset: 0,
          border: '1px solid rgba(255,255,255,0.08)',
          boxSizing: 'border-box',
          pointerEvents: 'none',
        }}
      />

      <div data-testid="outro-layout" style={{ position: 'absolute', inset: 0, overflow: 'hidden', color: '#FFFFFF' }}>
        <header style={{ position: 'relative', zIndex: 1, height: 84, display: 'flex', justifyContent: 'flex-end', alignItems: 'flex-start', padding: '24px 40px 0', boxSizing: 'border-box' }}>
          {logoUrl ? <img src={logoUrl} alt="" style={{ height: 28, maxWidth: 220, objectFit: 'contain' }} /> : null}
        </header>

        <main style={{ position: 'relative', zIndex: 1, padding: '60px 40px 0', display: 'flex', flexDirection: 'column', gap: 47 }}>
          <TextClamp as="h1" minLines={1} maxLines={1} lineHeight="1.2" style={{ fontFamily: 'var(--ds-font-display)', fontSize: 40, fontWeight: 400, color: '#FFFFFF', margin: 0 }}>
            {sanitizeNarrativeText(props.title) || ''}
          </TextClamp>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 36, width: 1320 }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12, minWidth: 0 }}>
              <SectionTable section={props.studyIdentity} testId="outro-study-identity" renderMode={renderMode} />
              <SectionTable section={props.studyInfo} testId="outro-study-info" renderMode={renderMode} />
              {sampleQuestions.map((section) => (
                <DistributionSection
                  key={section.title}
                  testId="outro-sample-question"
                  title={section.title}
                  rows={section.rows}
                  caption={section.caption}
                />
              ))}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12, minWidth: 0 }}>
              <SectionTable section={props.audienceProfile} testId="outro-audience-profile" renderMode={renderMode} />
              {distributions.map((section) => (
                <DistributionSection
                  key={section.title}
                  title={section.title}
                  rows={section.rows}
                  caption={section.caption}
                />
              ))}
            </div>
          </div>
        </main>

        <TextClamp minLines={1} maxLines={1} lineHeight="1.6" style={{ position: 'absolute', left: 40, bottom: 49, zIndex: 1, fontSize: 16, color: TEXT_MUTED }}>
          {props.footerText || ''}
        </TextClamp>
      </div>
    </SlideFrame>
  )
}

export const OutroSlide = registerSlide('outro', OutroSlideComponent)
